/**
 * The tiny markup language the homepage hero is edited in, and the rules that
 * keep a Slack edit from being able to break the page.
 *
 * Deliberately NOT HTML. The copy is written from a Slack modal by whoever has
 * the workspace, and it is rendered into the homepage's <h1>. Accepting HTML
 * would mean `dangerouslySetInnerHTML` — i.e. any workspace member could inject
 * a <script> into the logged-out homepage — and it would also let a paste of
 * styled text override the hero's type scale, gradient and responsive sizing.
 * So the editor gets two markers and a line break, everything else is text, and
 * every span is rendered as a React element that React escapes by construction.
 *
 *   **word**   → the brand accent (the gradient word in the headline)
 *   __word__   → bold
 *   Enter      → a line break
 *
 * `__` (not `*`) is bold on purpose. Single asterisks appear in real marketing
 * copy — "Free*", "rated 5*" — and a `*word*` rule would silently turn the text
 * BETWEEN two such asterisks bold. Underscores never show up that way in a
 * hero, so the rule can't misfire on legitimate copy.
 *
 * This module is dependency-free (no next/cache, no supabase) so the same
 * constants and parser can be used by the client homepage, the Slack routes and
 * unit tests without dragging a server-only import into the browser bundle.
 */

/**
 * Raw character caps, shared by the Slack modal's `max_length` and the
 * submission validator so the two can never disagree.
 *
 * Sized from the design, not from the database column. The v15 headline renders
 * at clamp(40px,7vw,64px) inside an 860px box — about three lines at 80 chars —
 * and the subheading at ~16px inside 540px. The fields used to allow 200/400,
 * which is several times what the layout can hold: a well-meaning editor could
 * save a paragraph and push the prompt box (the page's only conversion action)
 * below the fold on every phone.
 *
 * Counted on the RAW string, markers included, because that is what Slack's own
 * character counter in the modal shows the person typing.
 */
export const HERO_HEADING_MAX_LENGTH = 80;
export const HERO_SUBHEADING_MAX_LENGTH = 180;

/** The hint shown under both Slack inputs. Kept here so it can't drift from the parser. */
export const HERO_MARKUP_HINT =
  "Wrap a word in **double asterisks** to colour it, or __double underscores__ to bold it. Press Enter for a line break.";

/** One run of text and how it is painted. */
export interface HeroSpan {
  text: string;
  /** Rendered in the brand gradient. */
  accent: boolean;
  /** Rendered heavier than the surrounding copy. */
  bold: boolean;
}

/** A single rendered line; the caller joins lines with <br>. */
export type HeroLine = HeroSpan[];

/**
 * Matches one marked run. The accent alternative is listed first so `**x**`
 * wins over the bold rule when both could apply. `[^*]` / `[^_]` keep a run
 * from swallowing the rest of the line when a closing marker is missing —
 * without it, one stray `**` would paint everything after it.
 *
 * Rebuilt per call rather than shared at module scope: `String.split` is safe
 * with a sticky-free global regex, but `RegExp.test` on a `/g` literal advances
 * `lastIndex` between calls, so a shared instance would answer differently on
 * identical input depending on what was parsed before it.
 */
function markedRun(): RegExp {
  return /(\*\*[^*]+\*\*|__[^_]+__)/g;
}

const ACCENT_RUN = /^\*\*([^*]+)\*\*$/;
const BOLD_RUN = /^__([^_]+)__$/;

/**
 * C0 control characters (minus newline and tab), the zero-width family, and the
 * BOM. A paste out of a doc or a chat client carries these invisibly; in an
 * <h1> they do nothing except corrupt the string and inflate the character
 * count against the caps above.
 */
const INVISIBLE =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200D\uFEFF]/g;

/**
 * Canonicalise what the editor typed before it is stored.
 *
 * Runs on the way IN (at save time) rather than at render time so the database
 * holds exactly what the page will show — otherwise the Slack modal re-opens
 * pre-filled with a string that differs from the live hero.
 */
export function normalizeHeroText(raw: string): string {
  return raw
    .replace(/\r\n?/g, "\n")
    .replace(INVISIBLE, "")
    // Trailing spaces on a line survive an outer trim() and, before a <br>,
    // silently widen the balanced-wrap calculation.
    .replace(/[ \t]+$/gm, "")
    // Two blank lines in a hero is already a design accident; more is a gap
    // that pushes the prompt box off the screen.
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function unwrap(run: string): HeroSpan {
  let text = run;
  let accent = false;
  let bold = false;

  if (ACCENT_RUN.test(text)) {
    accent = true;
    text = text.slice(2, -2);
  } else if (BOLD_RUN.test(text)) {
    bold = true;
    text = text.slice(2, -2);
  }

  // One nested layer, so `**__word__**` and `__**word**__` both mean "accent
  // AND bold" instead of printing the inner markers as literal characters.
  if (!accent && ACCENT_RUN.test(text)) {
    accent = true;
    text = text.slice(2, -2);
  } else if (!bold && BOLD_RUN.test(text)) {
    bold = true;
    text = text.slice(2, -2);
  }

  return { text, accent, bold };
}

/**
 * Split edited copy into lines of painted spans. Total function: any string in,
 * something renderable out. Unmatched markers are left as literal text here —
 * `heroMarkupError` is what stops them reaching the page, at save time, where
 * the person who typed them can still fix it.
 */
export function parseHeroMarkup(text: string): HeroLine[] {
  return text.split("\n").map((line) =>
    line
      .split(markedRun())
      .filter((part) => part.length > 0)
      .map((part) =>
        ACCENT_RUN.test(part) || BOLD_RUN.test(part)
          ? unwrap(part)
          : { text: part, accent: false, bold: false },
      ),
  );
}

/** The words alone, markers removed — what the visitor actually reads. */
export function heroVisibleText(text: string): string {
  return parseHeroMarkup(text)
    .map((line) => line.map((span) => span.text).join(""))
    .join("\n");
}

/**
 * Why this copy must not be saved, or null when it is fine.
 *
 * Validating at the door rather than defending at render time is deliberate:
 * an unmatched `**` is a typo, and the person who made it is standing in front
 * of the modal. Rejecting it there costs them one correction; letting it
 * through prints raw asterisks across the homepage until somebody notices.
 */
export function heroMarkupError(
  raw: string,
  field: "heading" | "subheading",
): string | null {
  const label = field === "heading" ? "Heading" : "Subheading";
  const max =
    field === "heading" ? HERO_HEADING_MAX_LENGTH : HERO_SUBHEADING_MAX_LENGTH;
  const text = normalizeHeroText(raw);

  if (heroVisibleText(text).trim().length === 0) {
    return `${label} can't be empty.`;
  }

  if (text.length > max) {
    return `${label} is ${text.length} characters — keep it under ${max} so it fits the homepage layout.`;
  }

  // Any marker still present AFTER a run has been unwrapped was never part of a
  // matched pair (`Get **customers`) or was nested inside one in a way this
  // markup does not express (`**bold and __inner__ text**`). Both would print
  // the raw characters on the page.
  for (const line of parseHeroMarkup(text)) {
    for (const span of line) {
      if (span.text.includes("**")) {
        return `${label} has an unmatched ** — wrap the word on both sides, e.g. **customers**.`;
      }
      if (span.text.includes("__")) {
        return `${label} has an unmatched __ — wrap the word on both sides, e.g. __faster__.`;
      }
    }
  }

  return null;
}

/** How much of the headline the variant label carries before it is truncated. */
const VARIANT_LABEL_MAX = 60;

/**
 * FNV-1a, 4 hex digits. Same shape as the hash behind `trialCohort` and
 * `resolveInlinePublishCtaVariant`, namespaced by its own input so it cannot
 * move in lockstep with either.
 */
function fingerprint(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0").slice(0, 4);
}

/**
 * A stable label for one version of the hero copy, e.g.
 * `Build your app. Get customers. #7c1a`.
 *
 * This is the `homepage_variant` property on `homepage_viewed`, and it is what
 * makes "did the new copy convert better?" answerable. Two halves, both needed:
 *
 *   • The visible headline, so a row in PostHog says what the visitor actually
 *     read. A hash alone would force whoever reads the funnel to keep their own
 *     lookup table of which id meant which words, which nobody ever does.
 *   • A fingerprint of the heading AND subheading, so a change to the
 *     subheading alone — or an edit past the truncation point — still counts as
 *     a new variant. Without it those edits would silently pool into the
 *     previous variant's numbers and flatten exactly the difference the ticket
 *     is trying to measure.
 *
 * Derived from the copy rather than assigned, so it needs no registry and no
 * deploy: every Slack save produces its own variant automatically.
 */
export function heroVariantId(heading: string, subheading: string): string {
  const label = heroVisibleText(heading)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, VARIANT_LABEL_MAX);
  return `${label} #${fingerprint(`${heading} ${subheading}`)}`;
}

/**
 * A value safe to hand Slack as an input's `initial_value`.
 *
 * Slack rejects the whole `views.open` call — the editor simply refuses to
 * open — when `initial_value` is longer than `max_length`. That can only happen
 * for a row saved before these caps existed, but "the Slack command is broken"
 * is a much worse failure than "the pre-filled text is trimmed and has to be
 * re-approved", so the length is clamped rather than trusted.
 */
export function clampForSlack(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max);
}
